/* eslint-disable @typescript-eslint/no-use-before-define */
import NoSleep from '@uriopass/nosleep.js';
import { browser } from '../../../common/services/browser';
import { RootScopeEvents } from '../../../common/services/root-scope-types';
import { AngularServices, getAngularService } from '../../core';
import { isVideoPlaying } from './video';
const FAKE_VIDEO_ID = 'active-fake-video';
const noSleep = new NoSleep();
export function keepBrowserActive($rootScope) {
    if (typeof AudioContext !== 'function') {
        console.warn('AudioContext is not supported');
        return;
    }
    playFakeVideoOnInitialInteraction();
    // unmute video created in hidden mode
    playNewStreamInFakeVideoInHiddenMode($rootScope);
}
/**
 * @description  Video of participants became muted if no media enabled before.
 * @issue        https://doxyme.atlassian.net/browse/CS-5513
 * @workaround   Constantly play a fake(invisible) video on first user click that is not visible on the page.
 *               https://github.com/WebKit/WebKit/blob/f7cda68aefd4967036446c3855e995441a2f3a97/Source/WebCore/html/HTMLMediaElement.cpp#L8284
 **/
function playFakeVideoOnInitialInteraction() {
    if (document.querySelector(`#${FAKE_VIDEO_ID}`)) {
        return;
    }
    document.body.addEventListener('click', addAndPlayFakeVideo, {
        once: true,
    });
}
/**
 * @description  If video created while tab in hidden mode it is automatically muted.
 * @issue        https://doxyme.atlassian.net/browse/CS-5544
 * @workaround   As we already have a video element playing quietly on the background we can during hidden mode append
 *               a participant stream to it. On visible mode change it back. :)
 **/
function playNewStreamInFakeVideoInHiddenMode($rootScope) {
    $rootScope.$on(RootScopeEvents.SUBSCRIBER.PARTICIPANT_VIDEO_APPEARED, (_e, { stream }) => {
        const WAIT_UNTIL_VIDEO_PLAYS = 2000;
        setTimeout(() => {
            const videoEl = stream.videoElement;
            if (!videoEl || !videoEl.srcObject) {
                return;
            }
            if (document.visibilityState === 'hidden' &&
                !videoEl.ended &&
                !isVideoPlaying(videoEl)) {
                const streamAudioTracks = videoEl.srcObject.getAudioTracks();
                addAudioTracksToFakeVideo(streamAudioTracks);
            }
        }, WAIT_UNTIL_VIDEO_PLAYS);
    });
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') {
            setAudioTracksToFakeVideo([getSilentAudioTrack()]);
        }
    });
}
function addAudioTracksToFakeVideo(tracks) {
    const fakeVideo = document.querySelector(`#${FAKE_VIDEO_ID}`);
    if (!fakeVideo?.srcObject) {
        return;
    }
    for (const track of tracks) {
        fakeVideo.srcObject.addTrack(track.clone());
    }
    fakeVideo.play().catch(() => undefined);
}
function setAudioTracksToFakeVideo(tracks) {
    const fakeVideo = document.querySelector(`#${FAKE_VIDEO_ID}`);
    if (!fakeVideo?.srcObject) {
        return;
    }
    const stream = fakeVideo.srcObject;
    // remove all previous audio tracks
    for (const track of stream.getAudioTracks()) {
        stream.removeTrack(track);
    }
    // add new audio tracks
    for (const track of tracks) {
        stream.addTrack(track);
    }
    fakeVideo.play().catch(() => undefined);
}
function addAndPlayFakeVideo() {
    const fakeVideo = document.createElement('video');
    fakeVideo.id = FAKE_VIDEO_ID;
    fakeVideo.style.cssText =
        'width: 1px; height: 1px; top:0; left:0; z-index: 9999; position: absolute';
    fakeVideo.playsInline = true;
    fakeVideo.srcObject = new MediaStream([getSilentAudioTrack()]);
    fakeVideo.play().catch(() => undefined);
    document.body.append(fakeVideo);
}
const getSilentAudioTrack = (() => {
    let track = null;
    return () => track ?? (track = createSilentAudioTrack());
})();
function createSilentAudioTrack() {
    const context = new AudioContext();
    const oscillator = context.createOscillator();
    const destination = oscillator.connect(context.createMediaStreamDestination());
    oscillator.start();
    const track = destination.stream.getAudioTracks()[0];
    track.enabled = false;
    return track;
}
export function disableAutoLockIfItCausesIssues() {
    const rootScope = getAngularService(AngularServices.RootScope);
    rootScope.$on(RootScopeEvents.USER_ACTIONS.VIDEO_DISABLED, () => {
        if (browser.isIosButNotSafari() && !noSleep.isEnabled) {
            noSleep.enable().catch(() => console.warn('NoSleep error'));
        }
    });
    rootScope.$on(RootScopeEvents.USER_ACTIONS.VIDEO_ENABLED, () => {
        if (noSleep.isEnabled) {
            noSleep.disable();
        }
    });
}
